export type PermissionAction =
  | 'create'
  | 'read'
  | 'update'
  | 'delete'
  | 'assign'
  | 'manage_mobile'
  | 'delete_box_movement'
  | 'delete_expense_movement'
  | 'show_location'
  | 'allow_discount'
  | 'allow_manual_discount'
  | 'allow_automatic_discount'
  | 'allow_historical_lend_date'
  | 'pay'
  | 'refinance'
  | 'update_constant'
  | 'delete_selected'
  | 'pdf'
  | 'adjust_box'
  | 'register_expenses'
  | 'export'
  | 'view_errors'
  | 'transfer'
  | 'update_quote'
  | 'manage_backup_alert'
  | 'toggle_visibility'
  | 'toggle_enabled'
  | 'manage_insurance_box';

export interface AuthPermissionData {
  id?: number;
  name?: string;
  action?: string | null;
  menu?: {
    route?: string | null;
  } | null;
}

export interface AuthUserData {
  is_system?: number | null;
  username?: string | null;
  role?: {
    name?: string | null;
    permissions?: AuthPermissionData[] | null;
  } | null;
}

export interface ResourcePermissions {
  canView: boolean;
  canCreate: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canManageMobile: boolean;
  canDeleteBoxMovement: boolean;
  canDeleteExpenseMovement: boolean;
  canShowLocation: boolean;
  canAllowDiscount: boolean;
  canAllowManualDiscount: boolean;
  canAllowAutomaticDiscount: boolean;
  canAllowHistoricalLendDate: boolean;
  canPay: boolean;
  canRefinance: boolean;
  canUpdateConstant: boolean;
  canDeleteSelected: boolean;
  canPdf: boolean;
  canAdjustBox: boolean;
  canRegisterExpenses: boolean;
  canExport: boolean;
  canViewErrors: boolean;
  canTransfer: boolean;
  canUpdateQuote: boolean;
  canManageBackupAlert: boolean;
  canToggleEnabled: boolean;
  canManageInsuranceBox: boolean;
  /**
   * Alias pensado para acciones de asignación (constantes, oficinas, etc.).
   * Por defecto se mapea a los mismos permisos que canUpdate.
   */
  canAssign: boolean;
}

const RESOURCE_ROUTE_ALIASES: Record<string, string[]> = {
  '/dashboard': ['/'],
  '/': ['/dashboard'],
  '/administrar/tipogasto': ['/administrar/gastos'],
  '/administrar/gastos': ['/administrar/tipogasto'],
  '/prestamos/gastos/list': ['/administrar/detallegasto'],
  '/administrar/cajas': ['/administrar/cajas/add'],
  '/administrar/cajas/detail': ['/administrar/cajas/add'],
  '/procesos/transferirprestamo': ['/procesos/TransferirPrestamo'],
};

const ACTION_FALLBACKS: Record<string, Partial<Record<PermissionAction, PermissionAction[]>>> = {
  '/prestamos/abonos/list': {
    allow_manual_discount: ['allow_discount'],
    allow_automatic_discount: ['allow_discount'],
  },
  '/administrar/bancos': {
    create: ['update'],
  },
};

const normalizeRoute = (route?: string | null) => {
  if (!route) {
    return '';
  }

  const normalized = route.startsWith('/') ? route : `/${route}`;
  return normalized.toLowerCase();
};

const normalizeAction = (action?: string | null) => (action ?? '').toLowerCase();

const getCandidateRoutes = (resourceRoute: string) => {
  const normalizedRoute = normalizeRoute(resourceRoute);
  const aliases = RESOURCE_ROUTE_ALIASES[normalizedRoute] ?? [];
  return new Set([normalizedRoute, ...aliases.map(normalizeRoute)]);
};

const getAllowedActions = (resourceRoute: string, action: PermissionAction) => {
  const normalizedRoute = normalizeRoute(resourceRoute);
  const fallbacks = ACTION_FALLBACKS[normalizedRoute]?.[action] ?? [];
  return new Set<PermissionAction>([action, ...fallbacks]);
};

const hasPermissionForAction = (
  authUserData: AuthUserData | null | undefined,
  resourceRoute: string,
  action: PermissionAction
) => {
  if (!authUserData?.role) {
    return false;
  }

  if (Number(authUserData.is_system ?? 0) === 1) {
    return true;
  }

  const permissions = authUserData.role.permissions ?? [];
  const candidateRoutes = getCandidateRoutes(resourceRoute);
  const allowedActions = getAllowedActions(resourceRoute, action);

  return permissions.some((permission) => {
    const permissionRoute = normalizeRoute(permission.menu?.route);
    const permissionAction = normalizeAction(permission.action) as PermissionAction;

    return candidateRoutes.has(permissionRoute) && allowedActions.has(permissionAction);
  });
};

export const getResourcePermissions = (
  authUserData: AuthUserData | null | undefined,
  resourceRoute: string
): ResourcePermissions => {
  const canView = hasPermissionForAction(authUserData, resourceRoute, 'toggle_visibility');
  const canCreate = hasPermissionForAction(authUserData, resourceRoute, 'create');
  const canRead = hasPermissionForAction(authUserData, resourceRoute, 'read');
  const canUpdate = hasPermissionForAction(authUserData, resourceRoute, 'update');
  const canDelete = hasPermissionForAction(authUserData, resourceRoute, 'delete');
  const canAssign = hasPermissionForAction(authUserData, resourceRoute, 'assign');
  const canManageMobile = hasPermissionForAction(authUserData, resourceRoute, 'manage_mobile');
  const canDeleteBoxMovement = hasPermissionForAction(authUserData, resourceRoute, 'delete_box_movement');
  const canDeleteExpenseMovement = hasPermissionForAction(authUserData, resourceRoute, 'delete_expense_movement');
  const canShowLocation = hasPermissionForAction(authUserData, resourceRoute, 'show_location');
  const canAllowDiscount = hasPermissionForAction(authUserData, resourceRoute, 'allow_discount');
  const canAllowManualDiscount = hasPermissionForAction(authUserData, resourceRoute, 'allow_manual_discount');
  const canAllowAutomaticDiscount = hasPermissionForAction(authUserData, resourceRoute, 'allow_automatic_discount');
  const canAllowHistoricalLendDate = hasPermissionForAction(authUserData, resourceRoute, 'allow_historical_lend_date');
  const canPay = hasPermissionForAction(authUserData, resourceRoute, 'pay');
  const canRefinance = hasPermissionForAction(authUserData, resourceRoute, 'refinance');
  const canUpdateConstant = hasPermissionForAction(authUserData, resourceRoute, 'update_constant');
  const canDeleteSelected = hasPermissionForAction(authUserData, resourceRoute, 'delete_selected');
  const canPdf = hasPermissionForAction(authUserData, resourceRoute, 'pdf');
  const canAdjustBox = hasPermissionForAction(authUserData, resourceRoute, 'adjust_box');
  const canRegisterExpenses = hasPermissionForAction(authUserData, resourceRoute, 'register_expenses');
  const canExport = hasPermissionForAction(authUserData, resourceRoute, 'export');
  const canViewErrors = hasPermissionForAction(authUserData, resourceRoute, 'view_errors');
  const canTransfer = hasPermissionForAction(authUserData, resourceRoute, 'transfer');
  const canUpdateQuote = hasPermissionForAction(authUserData, resourceRoute, 'update_quote');
  const canManageBackupAlert = hasPermissionForAction(authUserData, resourceRoute, 'manage_backup_alert');
  const canToggleEnabled = hasPermissionForAction(authUserData, resourceRoute, 'toggle_enabled');
  const canManageInsuranceBox = hasPermissionForAction(authUserData, resourceRoute, 'manage_insurance_box');

  return {
    canView,
    canCreate,
    canRead,
    canUpdate,
    canDelete,
    canManageMobile,
    canDeleteBoxMovement,
    canDeleteExpenseMovement,
    canShowLocation,
    canAllowDiscount,
    canAllowManualDiscount,
    canAllowAutomaticDiscount,
    canAllowHistoricalLendDate,
    canPay,
    canRefinance,
    canUpdateConstant,
    canDeleteSelected,
    canPdf,
    canAdjustBox,
    canRegisterExpenses,
    canExport,
    canViewErrors,
    canTransfer,
    canUpdateQuote,
    canManageBackupAlert,
    canToggleEnabled,
    canManageInsuranceBox,
    canAssign,
  };
};
